import { useMemo } from 'react'
import { useGetSweepstakesByTypeWithGrouping } from 'hooks/api/useGetSweepstakesByTypeWithGrouping'
import { useGetLicensedEstablishmentsByCorporateAccountId } from 'hooks/api/useGetLicensedEstablishment'
import { useGetLicensedEstablishmentsByOrganizationId } from 'hooks/api/useGetLicensedEstablishmentsByOrganizationId'
import { type Sweepstake } from 'types/api'

const isSweepstake = (value: unknown): value is Sweepstake => {
  if (!value || typeof value !== 'object') {
    return false
  }

  return 'id' in value && 'name' in value
}

const getSweepstakeLicensedEstablishmentIds = (
  sweepstake: Sweepstake
): number[] => [
  ...(sweepstake.licensedEstablishmentIds ?? []).map(Number),
  ...(sweepstake.licensedEstablishments ?? []).map((licensedEstablishment) =>
    Number(licensedEstablishment.id)
  ),
]

// Filters active location sweepstakes down to those whose participating locations
// overlap with the licensed establishments implied by the selected badge subtype.
export const useLocationScopedSweepstakes = ({
  subtype,
  licensedEstablishmentIds,
  corporateAccountId,
  organizationId,
}: {
  subtype?: string
  licensedEstablishmentIds?: unknown
  corporateAccountId?: unknown
  organizationId?: unknown
}) => {
  const locationSweepstakesQuery = useGetSweepstakesByTypeWithGrouping({
    type: 'LocationSweepstake',
  })

  const availableLocationSweepstakes = useMemo(() => {
    const allLocationSweepstakes = Array.isArray(locationSweepstakesQuery.data)
      ? locationSweepstakesQuery.data
      : []

    return allLocationSweepstakes.filter(
      (value): value is Sweepstake =>
        isSweepstake(value) && value.sweepstakeData?.state !== 'Closed'
    )
  }, [locationSweepstakesQuery.data])

  const corporateAccountLicensedEstablishmentsQuery =
    useGetLicensedEstablishmentsByCorporateAccountId({
      corporateAccountId:
        subtype === 'CorporateAccount'
          ? Number(corporateAccountId) || undefined
          : undefined,
    })

  const organizationLicensedEstablishmentsQuery =
    useGetLicensedEstablishmentsByOrganizationId({
      organizationId: Number(organizationId) || 0,
      enabled: subtype === 'Organization' && Number(organizationId) > 0,
    })

  const scopedLicensedEstablishmentIds = useMemo(() => {
    if (subtype === 'Location') {
      return new Set(
        (Array.isArray(licensedEstablishmentIds)
          ? licensedEstablishmentIds
          : []
        )
          .map(Number)
          .filter((id) => !Number.isNaN(id))
      )
    }

    if (subtype === 'CorporateAccount') {
      return new Set(
        corporateAccountLicensedEstablishmentsQuery.data?.map((le) =>
          Number(le.id)
        ) ?? []
      )
    }

    if (subtype === 'Organization') {
      return new Set(
        organizationLicensedEstablishmentsQuery.data?.map((le) =>
          Number(le.id)
        ) ?? []
      )
    }

    return new Set<number>()
  }, [
    subtype,
    licensedEstablishmentIds,
    corporateAccountLicensedEstablishmentsQuery.data,
    organizationLicensedEstablishmentsQuery.data,
  ])

  const locationScopedSweepstakes = useMemo(() => {
    if (scopedLicensedEstablishmentIds.size === 0) {
      return availableLocationSweepstakes
    }

    return availableLocationSweepstakes.filter((sweepstake) =>
      getSweepstakeLicensedEstablishmentIds(sweepstake).some((id) =>
        scopedLicensedEstablishmentIds.has(id)
      )
    )
  }, [availableLocationSweepstakes, scopedLicensedEstablishmentIds])

  const regularLocationSweepstakes = useMemo(() => {
    return locationScopedSweepstakes.filter(
      (sweepstake: Sweepstake) => !sweepstake.groupId
    )
  }, [locationScopedSweepstakes])

  const groupedLocationSweepstakes = useMemo(() => {
    const groupedById = new Map<string, Sweepstake>()

    for (const sweepstake of locationScopedSweepstakes) {
      if (sweepstake.groupId && !groupedById.has(sweepstake.groupId)) {
        groupedById.set(sweepstake.groupId, sweepstake)
      }
    }

    return Array.from(groupedById.values())
  }, [locationScopedSweepstakes])

  return {
    regularLocationSweepstakes,
    groupedLocationSweepstakes,
    isPending: locationSweepstakesQuery.isPending,
  }
}
